//eslint:disable max-func-body-length
import { ValidationProps } from 'ui/common/validation/types';
import { STATE } from 'app/constants';
import { ActionDispatching, UserMessageType } from 'app/types-shared';
import { ApplicationState } from 'app/types-store';
import { routeConstants } from 'app/routes/route-constants';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { createDescriptionSelector } from 'app/infrastructure/translate';
import * as navigate from 'app/area/shared/navigation';
import { complexValidationFromServer } from 'app/infrastructure/data/validation';
import { ErrorNotification, SuccessNotification } from 'app/infrastructure/notifications/user-messages';
import { FilterOption } from 'app/components/options-handler/types';
import { hasTranslationsValid } from 'app/components/translation/has-translations-valid';
import { isForbiddenStatus, isNotFoundStatus, blockAccess, isBadRequest } from 'app/area/shared/actions-utils';
import { ClearCodesAccess } from '../action-types';
import { codetableOptionsActionCreators } from '../common';
import { api as commonApi } from '../common/api';
import { codetableAdministrationTranslation } from '../constants';
import { getCodetableId } from 'app/area/shared/codetable/breadcrumbs';
import { CodetableAdministrationState, CodetableFeature, CodeFieldWireKey } from '../types-store';
import { CodeComplexValue } from '../../shared/codetable/types';
import { mapCopyTranslations } from './mapping/copy-translations';
import { createEmptyCodeItem } from './mapping/create-empty-code-item';
import { mapCodeItemSubset, mapSubstanceUomSet, mapUomConversionFactors, mapCodeUomSets } from './mapping/map-code-item';
import { mapCodeToStore } from './mapping/map-code-to-store';
import { mapCodeToWire, mapColourCode, mapToZeroValuesPerScreens, mapUomConversion } from './mapping/map-code-to-wire';
import { ScreenPicker, ScreenField, SupportedCodeScreens } from './screen-renderer/types';
import { mapToSubstanceUomSet } from './screen-renderer/screen-pickers/substance/sets/uom-sets-mapper';
import { mapToSubstanceConversionFactorValidation } from './screen-renderer/screen-validate/uom/validate';
import { hasValidationErrors } from './validation/has-validation-errors';
import {
  RequestCodeItem, ReceiveCodeItem, FailCodeItem, CopyDescriptionToAllTranslations,
  RequestSaveCodetableItem, ReceiveSaveCodetableItem, FailSaveCodetableItem,
  RequestCodeItemDelete, ReceiveCodeItemDelete, FailCodeItemDelete,
  UpdateListNode, UpdateParent, UpdateDisabled, UpdateSequence, UpdateDescription, UpdateLongDescription,
  UpdateUserGroups, UpdateExcludedFields, UpdateCaseTypes, UpdateCodeCategory,
  RequestCreateCodeItem, ReceiveCreateCodeItem, FailCreateCodeItem,
  ResetDescriptionValidation, ResetLongDescriptionValidation,
  UpdateValidation, ResetValidation, ClearValidation, CopyLongDescriptionToAllTranslations,
  SwitchTranslationsToggle, ClearCodetableOptions, ResetAllDescriptionsValidation, ResetAllLongDescriptionsValidation,
  UpdateCodetableTypes, UpdateIcon, UpdateColour, UpdateColourCode, UpdateByKey, UpdateOriginalCodeByKey, RequestParentCategory,
  ReceiveParentCategory, FailParentCategory, UpdateMeasurementSystem,
  UpdateUomConversion, UpdateUomConversionFactorItem, RequestCheckIsUomExcluded, ReceiveCheckIsUomExcluded, FailCheckIsUomExcluded,
  RequestUomConversionFactors, ReceiveUomConversionFactors, FailUomConversionFactors, ClearUomConversionFactors, ClearUomExcluded,
  CreateInitialUomConversionFactors, UpdateMergeCode, ToggleDuplicateHierarchy
} from './action-types';
import {
  getParent, loadAllOptions, getCategory, loadLanguages,
  getParentFullPath, createCodeItem, shouldCreateCodetableItem
} from './actions-helpers';
import { api } from './api';
import { createConversionFactorsId, createSafetyLimitId } from './codetable-item-helpers';
import { substanceCodetableIds, unitOfMeasureExcludeId, unitOfMeasureTypeId } from './constants';
import { CodeItem, DeleteCodeItemPayload, SubstanceConversionFactor, SafetyLimit } from './types-store';
import {
  SaveCodetableItem, CreateCodeItemProps, CopyTranslationsPayload, CodeActionType,
  UpdateConversionFactorPayload, UpdateSafetyLimitPayload
} from './types';
import { SupportedListScreens } from 'app/area/codetable/list/list-renderer/supported-screens';
import { MergeCodeState } from 'app/area/codetable/common/merge-code/types-store';
import { escapeUrl } from 'app/components/favourite-layout/favourite/common/utils';

export const actionCreators = {

  ...codetableOptionsActionCreators,

  createCodeItem: (props: CreateCodeItemProps): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      const { metadata: { data: codetableMetadata } } = getState().administrationCodetable;
      const { parentId } = props;

      dispatch(new RequestCreateCodeItem());

      try {

        await loadAllOptions(props, codetableMetadata);

      } catch (err) {

        dispatch(new FailCreateCodeItem());

        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));

        return;
      }

      const { item: { codetableOptions }, options, list } =
        getState().administrationCodetable as CodetableAdministrationState & { options: { [key: string]: FilterOption } };

      const storedCategory = !!list && !!list.category ? list.category : undefined;

      const parent = getParent(parentId, codetableOptions);
      const parentFullPath = getParentFullPath(parentId, codetableOptions);
      const parentWithFullPath = { ...parent, description: parentFullPath };

      const category = await getCategory(storedCategory, props, codetableMetadata);

      const codeItem: CodeItem = createEmptyCodeItem({
        metadata: codetableMetadata,
        caseTypes: options ? options.caseTypes : undefined,
        userGroups: options ? options.userGroups : undefined,
        parent: parentWithFullPath,
        languages: options ? options.languages : undefined,
        category,
        parentId,
        ...(codetableOptions && codetableOptions.uomSets && { substanceUomSet: mapToSubstanceUomSet(codetableOptions.uomSets.codes) })
      });

      dispatch(new ReceiveCreateCodeItem(codeItem));
      dispatch(new SwitchTranslationsToggle(false));
    },

  loadCodeItem: (props: CreateCodeItemProps): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      const { codeId, parentId, loadOptions, loadCodetableOptions, breadcrumbs, locale } = props;

      const codetableId = getCodetableId(breadcrumbs);

      dispatch(new RequestCodeItem(codetableId, codeId));

      if (isDefinedIncludingZero(parentId)) {
        try {

          await loadCodetableOptions('parent', codetableId, { parentId });

        } catch (err) {

          dispatch(new FailCodeItem(codetableId, codeId));

          const error = err && (err.userMessages || err.message);
          error && dispatch(new ErrorNotification(error));

          return;
        }
      }

      const shouldLoadUomSetOptions = codetableId === SupportedCodeScreens.SubstanceCategory
        || codetableId === SupportedCodeScreens.SubstanceHierarchy;

      if (shouldLoadUomSetOptions) {
        try {

          await loadCodetableOptions('uomSets', substanceCodetableIds.uomSets, { includeNotSelected: false });

        } catch (err) {

          const error = err && (err.userMessages || err.message);
          error && dispatch(new ErrorNotification(error));
          return;
        }
      }

      const { metadata: { data: codetableMetadata } = {},
        access: { data: { codes: accessCodes } = {} } = {},
        item: { codetableOptions } = {}
      } = getState().administrationCodetable;

      try {

        const codeItemWire = await Promise.all([
          api.loadCodeItem(codetableId, codeId),
          loadLanguages(codetableMetadata, loadOptions)
        ]);

        const codeItemWithUomSets = mapCodeUomSets(
          mapSubstanceUomSet(
            codetableId,
            codeItemWire[0]
          ),
          codetableOptions?.uomSets?.codes
        );

        const codeItem = mapCodeToStore({
          ...mapCodeItemSubset(
            codeItemWithUomSets,
            accessCodes)
        });

        const parent = getParent(parentId, codetableOptions);
        const parentFullPath = getParentFullPath(parentId, codetableOptions);
        const parentWithFullPath = { ...parent, description: parentFullPath };
        const descendantCount = codeItemWire[0]?.descendantCount;

        dispatch(new ReceiveCodeItem({
          codetableId,
          codeId,
          descendantCount,
          codeItem: createCodeItem(props.actionType, codeItem, codetableMetadata, parentWithFullPath, parentId, locale)
        }));

        dispatch(new SwitchTranslationsToggle(true));

      } catch (err) {

        if (isBadRequest(err) || isNotFoundStatus(err)) {
          navigate.toStartupView();
        }

        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.administrationCodetable,
            translationId: codetableAdministrationTranslation.no_access_to_codetable_administration
          });
        } else if (err && (err.userMessages || err.message)) {
          const error = err && (err.userMessages || err.message);
          error && dispatch(new ErrorNotification(error));
        }

        dispatch(new FailCodeItem(codetableId, codeId));
      }
    },

  createUomConversionFactors: (
    id: number,
    codetableId: number,
    categoryId: number,
    actionType: CodeActionType,
    uomConversion: CodeComplexValue<number>[]
  ): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {

      dispatch(new RequestUomConversionFactors());

      try {
        const result = await api.loadUomConversionOptions(codetableId, categoryId);

        const wireFactors = result?.results || [];

        const mapped = mapUomConversionFactors(id, actionType, wireFactors, uomConversion);

        result && dispatch(new ReceiveUomConversionFactors(mapped));
      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailUomConversionFactors());
      }
    },

  createInitialUomConversionFactors: (uomConversionFactors: CodeComplexValue<number>[]): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      dispatch(new CreateInitialUomConversionFactors(uomConversionFactors));
    },

  deleteCodeItem: (payload: DeleteCodeItemPayload): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      const { codeId, successMessage, breadcrumbs, reassignReferencesToCode } = payload;

      const codetableId = getCodetableId(breadcrumbs);

      dispatch(new RequestCodeItemDelete(codetableId, codeId));

      try {
        await commonApi.deleteCodeItem(codetableId, codeId, reassignReferencesToCode);

        navigate.to({ pathname: routeConstants.codetableAdministrationFor(breadcrumbs) });

        dispatch(new ReceiveCodeItemDelete(codeId));
        dispatch(new UpdateMergeCode(undefined));
        dispatch(new SuccessNotification(successMessage));
      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));

        dispatch(new FailCodeItemDelete(codeId));

        if (Object.keys(err?.validationErrors ?? {}).length > 0) {
          dispatch(new UpdateValidation(complexValidationFromServer(err.validationErrors)));
        }
      }
    },

  updateListNode: (node: CodeComplexValue<number>): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new ResetValidation(CodetableFeature.node));
    dispatch(new UpdateListNode(node));
  },

  updateParent: (parent: CodeComplexValue<number>, codetableId: number, shouldLoadParentCategory?: boolean): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      dispatch(new ResetValidation(CodetableFeature.parent));
      dispatch(new UpdateParent(parent));

      const codeId = parent && parent.value;

      if (!shouldLoadParentCategory || !isDefinedIncludingZero(codetableId) || !isDefinedIncludingZero(codeId)) {
        return;
      }

      dispatch(new RequestParentCategory());

      try {
        const codeItemWire = await api.loadCodeItem(codetableId, codeId);
        const codeItem = mapCodeToStore(codeItemWire);

        dispatch(new UpdateCodeCategory(codeItem ? codeItem.category : undefined));
        dispatch(new ReceiveParentCategory());
      } catch (err) {
        dispatch(new FailParentCategory());
        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.administrationCodetable,
            translationId: codetableAdministrationTranslation.no_access_to_codetable_administration
          });
        } else if (err && (err.userMessages || err.message)) {
          const error = err && (err.userMessages || err.message);
          error && dispatch(new ErrorNotification(error));
        }

        if (isBadRequest(err)) {
          navigate.toStartupView();
        }
      }
    },

  updateDescription: (languageId: number, description: CodeComplexValue<number>[]): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {
      dispatch(new ResetDescriptionValidation(languageId));
      dispatch(new UpdateDescription(languageId, description));
    },

  updateLongDescription: (languageId: number, longDescription: CodeComplexValue<number>[]): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {
      dispatch(new ResetLongDescriptionValidation(languageId));
      dispatch(new UpdateLongDescription(languageId, longDescription));
    },

  updateDisabled: (disabled: Date): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new ResetValidation(CodetableFeature.disabled));
    dispatch(new UpdateDisabled(disabled));
  },

  updateUserGroups: (userGroup: CodeComplexValue<number>[]): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new UpdateUserGroups(userGroup));
  },

  updateExcludedFields: (excludedFields: CodeComplexValue<number>[]): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new ResetValidation(CodeFieldWireKey.exclude));
    dispatch(new UpdateExcludedFields(excludedFields));
  },

  updateCaseTypes: (caseType: CodeComplexValue<number>[]): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new UpdateCaseTypes(caseType));
  },

  updateCodetableTypes: (codetableTypes: CodeComplexValue<number>[]): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new UpdateCodetableTypes(codetableTypes));
  },

  updateCodeCategory: (codeCategory: CodeComplexValue<number>): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new ResetValidation(CodetableFeature.category));
    dispatch(new UpdateByKey(ScreenPicker.uom, { value: 0 }));
    dispatch(new UpdateCodeCategory(codeCategory));
    dispatch(new UpdateParent(undefined));
  },

  updateSequence: (sequence: number): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new ResetValidation(CodetableFeature.sequence));
    dispatch(new UpdateSequence(sequence));
  },

  updateIcon: (icon: string): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new ResetValidation(CodetableFeature.icon));
    dispatch(new UpdateIcon(icon));
  },

  updateColour: (colour: CodeComplexValue<number>): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new ResetValidation(CodetableFeature.colour));
    dispatch(new UpdateColour(colour ? colour.backgroundColor : undefined));
  },

  updateColourCode: (colourCode: CodeComplexValue<number>): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new ResetValidation(CodeFieldWireKey.colourCode));
    dispatch(new UpdateColourCode(colourCode));
  },

  updateMeasurementSystem: (measurementSystem: CodeComplexValue<number>): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new ResetValidation(CodeFieldWireKey.measurementSystem));
    dispatch(new UpdateMeasurementSystem(measurementSystem));
  },

  updateUomConversion: (uomConversion: CodeComplexValue<number>[]): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new UpdateUomConversion(uomConversion));
  },

  updateUomConversionFactorItem: (id: number, value: number): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new UpdateUomConversionFactorItem(id, value));
  },

  resetCodeItem: (): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new ReceiveCreateCodeItem(undefined));
    dispatch(new ClearCodetableOptions());
  },

  copyDescriptionToAllTranslations: (payload: CopyTranslationsPayload): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {
      const description = payload.features.description ? mapCopyTranslations(payload, CodetableFeature.description) : undefined;

      dispatch(new CopyDescriptionToAllTranslations(description));
      dispatch(new ResetAllDescriptionsValidation());
      dispatch(new SuccessNotification(payload.successText));
    },

  copyLongDescriptionToAllTranslations: (payload: CopyTranslationsPayload): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {
      const longDescription = payload.features.longDescription ? mapCopyTranslations(payload, CodetableFeature.longDescription) : undefined;

      dispatch(new CopyLongDescriptionToAllTranslations(longDescription));
      dispatch(new ResetAllLongDescriptionsValidation());
      dispatch(new SuccessNotification(payload.successText));
    },

  saveCodetableItem: (saveCodetableItem: SaveCodetableItem): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      const { metadata, codetableId, options, validationErrorMessage, mainLanguage, breadcrumbs,
        actionType, saveSubsetsOnly, isHierarchy, withDescendants } = saveCodetableItem;
      const { item: { codeItem: { id: codeId, uomConversion: codeItemUomConversion }, codeItem, isUomExcluded, uomConversionFactors,
        showDuplicateHierarchy, validation } } = getState().administrationCodetable;

      if (hasValidationErrors(validation)) {
        dispatch(new ErrorNotification(validationErrorMessage));
        !hasTranslationsValid(validation.language, mainLanguage.value) &&
          dispatch(new SwitchTranslationsToggle(true));
        return;
      }

      const codetableFound = !!metadata && !!metadata.codetable && metadata.codetable.id === codetableId ? metadata : undefined;

      dispatch(new RequestSaveCodetableItem());

      try {
        const languageOptions = (!!options && !!options.languages && options.languages.codes || []) as CodeComplexValue<number>[];

        let response;

        const mappedToZeroCodeItemProperties = mapToZeroValuesPerScreens({
          likelihood: {
            codetableId,
            supportedScreens: [SupportedCodeScreens.RiskArea, SupportedCodeScreens.ConsequencesLikelihood],
            value: codeItem.likelihood
          },
          recurrence: {
            codetableId,
            supportedScreens: [SupportedCodeScreens.RiskArea, SupportedCodeScreens.ConsequencesRecurrence],
            value: codeItem.recurrence
          },
          severity: {
            codetableId,
            supportedScreens: [SupportedCodeScreens.RiskArea, SupportedCodeScreens.ConsequencesSeverity,
            SupportedCodeScreens.SecurityBreachType],
            value: codeItem.severity
          },
          consequenceType: {
            codetableId,
            supportedScreens: [SupportedCodeScreens.InsuranceType],
            value: codeItem.consequenceType
          },
          uom: {
            codetableId,
            supportedScreens: [SupportedCodeScreens.ConsequencesSeverity],
            value: codeItem.uom
          }
        });

        const colourCode = mapColourCode(metadata, codeItem);

        const uomConversion = uomConversionFactors
          && mapUomConversion(codeItem?.id, actionType, isUomExcluded, codeItemUomConversion, uomConversionFactors);

        const mappedCodeItem = mapCodeToWire({
          codetableId,
          codeItem: { ...codeItem, colourCode, uomConversion, ...mappedToZeroCodeItemProperties },
          languageOptions,
          features: codetableFound.features,
          substanceUomSetType: saveCodetableItem.substanceUomSetType
        });

        const hierarchyWithDescendants = isHierarchy && withDescendants && actionType === CodeActionType.Duplicate;

        if (shouldCreateCodetableItem(codeId, saveCodetableItem.actionType)) {
          if (hierarchyWithDescendants) {
            response = await api.duplicateCodeWithDescendants(codetableId, codeId, mappedCodeItem);
          } else {
            response = await api.createCodetableItem(actionType, codetableId, codeId, mappedCodeItem);
          }
        } else {
          if (saveSubsetsOnly) {
            response = await api.saveSubsetsOnly(codetableId, codeId, mappedCodeItem);
          } else {
            response = await api.saveCodetableItem(codetableId, codeId, mappedCodeItem);
          }
        }

        if (!!response && !!response.validationErrors) {
          dispatch(new UpdateValidation(complexValidationFromServer(response.validationErrors)));
          return;
        }

        navigate.to({ pathname: routeConstants.codetableAdministrationFor(breadcrumbs) });

        dispatch(new ReceiveSaveCodetableItem());

        showDuplicateHierarchy && dispatch(new ToggleDuplicateHierarchy());

        let successfulSaveText;

        const successfulUpdateText = isHierarchy
          ? codetableAdministrationTranslation.hierarchical_codetable_update
          : codetableAdministrationTranslation.updated_successfully;

        if (actionType === CodeActionType.Duplicate) {
          successfulSaveText = codetableAdministrationTranslation.duplicated_successfully;
        } else {
          successfulSaveText = isDefinedIncludingZero(codeId)
            ? successfulUpdateText
            : codetableAdministrationTranslation.saved_successfully;
        }

        const areaState = getState().administrationCodetable;
        const translationDescription = createDescriptionSelector(areaState.texts);
        const savedSuccessfully = translationDescription(successfulSaveText);

        dispatch(new SuccessNotification(savedSuccessfully));

        if ([CodeActionType.Add, CodeActionType.Duplicate].includes(actionType) &&
          [SupportedCodeScreens.RecurrenceFrequency, SupportedCodeScreens.Severity, SupportedCodeScreens.Likelihood]
            .includes(codetableId)) {
          dispatch(new SuccessNotification([{
            id: 0,
            type: UserMessageType.success,
            link: `${SupportedListScreens.RiskMatrix}`,
            message: translationDescription(codetableAdministrationTranslation.risk_matrix_toast_reference)
          }]));
        }

      } catch (err) {
        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.administrationCodetable,
            translationId: codetableAdministrationTranslation.no_access_to_codetable_administration
          });
        }

        if (isNotFoundStatus(err)) {
          navigate.replace(escapeUrl(routeConstants.codetableAdministrationFor(breadcrumbs)));
        }

        const error = !!err ? err.userMessages : undefined;
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailSaveCodetableItem());

        if (!!err && !!err.validationErrors) {
          const substanceConversionFactorValidation = mapToSubstanceConversionFactorValidation(err.validationErrors);

          const mappedValidation = {
            ...complexValidationFromServer(err.validationErrors),
            substanceConversionFactor: undefined,
            ...substanceConversionFactorValidation
          };

          dispatch(new UpdateValidation(mappedValidation));
          !hasTranslationsValid(err.validationErrors, mainLanguage.value) &&
            dispatch(new SwitchTranslationsToggle(true));
          dispatch(new ErrorNotification(validationErrorMessage));
        }
      }
    },

  updateValidation: (validation: { [key: string]: ValidationProps }): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new UpdateValidation(validation));
  },

  clearValidation: () => (dispatch: any, getState: any) => {
    dispatch(new ClearValidation());
  },

  clearCodesAccess: () => (dispatch: any, getState: any) => {
    dispatch(new ClearCodesAccess());
  },

  switchTranslationsToggle: (toggled?: boolean) => (dispatch: any, getState: any) => {
    dispatch(new SwitchTranslationsToggle(toggled));
  },

  updateByKey: <T>(key: ScreenPicker | ScreenField, value: T): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new UpdateByKey(key, value));
  },

  clearByKey: (key: ScreenPicker | ScreenField): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new UpdateByKey(key, undefined));
  },

  clearOriginalCodeByKey: (key: ScreenPicker | ScreenField): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new UpdateOriginalCodeByKey(key, undefined));
  },

  clearUomConversionFactors: (): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new ClearUomConversionFactors());
  },

  clearUomExcluded: (): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new ClearUomExcluded());
  },

  clearFactorValidation: (factorIds: string[]): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    factorIds.forEach(key => dispatch(new ResetValidation(key)));
  },

  resetValidation: (key: ScreenPicker | ScreenField | string): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new ResetValidation(key));
  },

  codetableBlockAccess: (breadcrumbs: number[]) => (dispatch: any, getState: any) => {
    blockAccess({
      dispatch,
      getState,
      area: STATE.administrationCodetable,
      translationId: codetableAdministrationTranslation.no_access_to_codetable_administration
    });

    navigate.replace(routeConstants.codetableAdministrationFor(breadcrumbs));
  },

  removeConversionFactor: (codeItem: CodeItem, toRemove: SubstanceConversionFactor, validation: { [key: string]: ValidationProps }) =>
    (dispatch: any, getState: any) => {
      const filteredConversionFactors = codeItem?.substanceConversionFactor.filter(factor =>
        factor?.uomConversionType?.value !== toRemove?.uomConversionType?.value);

      dispatch(new UpdateByKey(ScreenPicker.substanceConversionFactor, filteredConversionFactors));

      dispatch(new UpdateValidation({
        ...validation,
        [createConversionFactorsId(ScreenPicker.uomFrom, toRemove?.uomConversionType?.value)]: undefined,
        [createConversionFactorsId(ScreenPicker.uomTo, toRemove?.uomConversionType?.value)]: undefined,
        [createConversionFactorsId(ScreenField.factor, toRemove?.uomConversionType?.value)]: undefined
      }));
    },

  updateConversionFactor: ({ value, codeItem, conversionFactor, key, validation }: UpdateConversionFactorPayload) =>
    (dispatch: any, getState: any) => {
      const conversionFactorIndex = codeItem?.substanceConversionFactor?.

        findIndex(factor => conversionFactor.uomConversionType.value === factor.uomConversionType.value);

      if (conversionFactorIndex === -1) {
        return;
      }
      const filteredConversionFactors = codeItem?.substanceConversionFactor.filter((_, index) => index !== conversionFactorIndex);

      const updatedConversionFactor: SubstanceConversionFactor = { ...conversionFactor, [key]: value };
      const updatedConversionFactors = [...filteredConversionFactors.slice(0, conversionFactorIndex),
        updatedConversionFactor, ...filteredConversionFactors.slice(conversionFactorIndex)];


      dispatch(new UpdateByKey(ScreenPicker.substanceConversionFactor, updatedConversionFactors));

      dispatch(new UpdateValidation({
        ...validation,
        [createConversionFactorsId(key as ScreenPicker, conversionFactor?.uomConversionType?.value)]: undefined
      }));
    },

  checkIsUomExcluded: (uomTypeId: number) =>
    async (dispatch: any, getState: any) => {
      if (!isDefinedIncludingZero(uomTypeId)) {
        return;
      }
      dispatch(new RequestCheckIsUomExcluded());

      try {
        const codeItemWire = await api.loadCodeItem(unitOfMeasureTypeId, uomTypeId);

        const codeItem = mapCodeToStore(codeItemWire);

        const isUomExcluded = !!codeItem.excludeFields.find(field => field.value === unitOfMeasureExcludeId);

        dispatch(new ReceiveCheckIsUomExcluded(isUomExcluded));
      } catch (err) {

        dispatch(new FailCheckIsUomExcluded());

        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
      }
    },

  updateMergeCode: (mergeCode: MergeCodeState) => (dispatch: any, getState: any) => {
    dispatch(new UpdateMergeCode(mergeCode));
  },

  toggleDuplicateHierarchy: () => (dispatch: any, getState: any) => {
    dispatch(new ToggleDuplicateHierarchy());
  },

  removeSafetyLimit: (codeItem: CodeItem, toRemove: SafetyLimit, validation: { [key: string]: ValidationProps }) =>
    (dispatch: any) => {
      const filteredSafetyLimits = codeItem?.safetyLimit.filter(item =>
        item?.application?.value !== toRemove?.application?.value);
      dispatch(new UpdateByKey(ScreenPicker.safetyLimit, filteredSafetyLimits));
      dispatch(new UpdateValidation({
        ...validation,
        [createSafetyLimitId(ScreenPicker.minValue, toRemove?.application?.value)]: undefined,
        [createSafetyLimitId(ScreenPicker.maxValue, toRemove?.application?.value)]: undefined,
        [createSafetyLimitId(ScreenField.warningDeviationPercent, toRemove?.application?.value)]: undefined
      }));
    },

  updateSafetyLimit: ({ value, codeItem, safetyLimit, key, validation }: UpdateSafetyLimitPayload) =>
    (dispatch: any) => {
      const safetyLimitIndex = codeItem?.safetyLimit?.findIndex(
        item => safetyLimit.application.value === item.application.value);
      if (safetyLimitIndex === -1) {
        return;
      }
      const filteredSafetyLimits = codeItem?.safetyLimit.filter((_, index) => index !== safetyLimitIndex);
      const updatedSafetyLimit: SafetyLimit = { ...safetyLimit, [key]: value };
      const updatedSafetyLimits = [...filteredSafetyLimits.slice(0, safetyLimitIndex),
        updatedSafetyLimit, ...filteredSafetyLimits.slice(safetyLimitIndex)];
      dispatch(new UpdateByKey(ScreenPicker.safetyLimit, updatedSafetyLimits));
      dispatch(new UpdateValidation({
        ...validation,
        [createSafetyLimitId(key as ScreenPicker, safetyLimit?.application?.value)]: undefined
      }));
    }
};
